import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import type { LngLat, Map, MapOptions, Marker, MarkerOptions } from 'maplibre-gl';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FeatureProperties, JawgPlacesResponse } from '../interfaces/jawgs-api.interface';
import { PlaceInfos } from '../interfaces/place-infos.interface';
import { LangPipe } from '../pipes/lang.pipe';
import { LazyLoaderService } from './lazy-loader.service';

// set 'jawgAccessToken: value' in environment file
export const JAWG_ACCESS_TOKEN = new InjectionToken('Access Token For Jawg API');

export type MapLibre = typeof import('maplibre-gl');

@Injectable({ providedIn: 'root' })
export class JawgMapsService {
  private readonly lazyLoaderService = inject(LazyLoaderService);
  private readonly langPipe = inject(LangPipe);
  private readonly http = inject(HttpClient);
  private readonly jawgAccessToken = inject(JAWG_ACCESS_TOKEN);

  private readonly jawgBaseApi = 'https://api.jawg.io/places/v1';

  maplibre: MapLibre;

  async loadMapLibre(): Promise<MapLibre> {
    if (!this.maplibre) {
      this.maplibre = await this.lazyLoaderService.loadLibrary('maplibre-gl', 'assets/maplibre/maplibre-gl.css');
    }

    return this.maplibre;
  }

  /**
   * Gets a formatted address string from coordinates by calling the Jawg reverse geocoding API
   * @param coordinates The coordinates (longitude/latitude) to get the address for
   * @returns Observable that emits a formatted address string. If no results found, returns localized "no geolocation" message
   */
  getAddressByCoordinates(coordinates: LngLat): Observable<string> {
    return this.http
      .get<JawgPlacesResponse>(
        `${this.jawgBaseApi}/reverse?point.lat=${coordinates.lat}&point.lon=${coordinates.lng}&access-token=${this.jawgAccessToken}`,
      )
      .pipe(
        map((res) => {
          let address = this.langPipe.transform('common-settings.__NO_GEOLOCATION');
          if (res.features.length > 0) {
            address = this.getFormattedAddress(res.features[0].properties);
          }
          return address;
        }),
        catchError(() => {
          const errorMessage = this.langPipe.transform('common-app.__ERROR_UNKNOWN');
          return of(errorMessage);
        }),
      );
  }

  getPlacesByText(text: string): Observable<PlaceInfos[]> {
    return from(this.loadMapLibre()).pipe(
      switchMap(() => {
        return this.http
          .get<JawgPlacesResponse>(
            `${this.jawgBaseApi}/autocomplete?text=${encodeURIComponent(text)}&size=5&access-token=${this.jawgAccessToken}`,
          )
          .pipe(
            map((placeInfosRequest) => {
              return placeInfosRequest.features.map((feature) => {
                const address = this.getFormattedAddress(feature.properties);
                const coordinates = new this.maplibre.LngLat(
                  feature.geometry.coordinates[0],
                  feature.geometry.coordinates[1],
                );

                return {
                  address,
                  coordinates,
                  ...feature,
                } as PlaceInfos;
              });
            }),
            catchError(() => {
              return of(null);
            }),
          );
      }),
    );
  }

  private getFormattedAddress(props: FeatureProperties): string {
    return [props.name, props.locality, props.postalcode, props.country].filter(Boolean).join(', ');
  }

  createMap(mapId: string, options?: Partial<MapOptions>): Map {
    if (!this.maplibre) throw new Error('MapLibre not loaded. Call loadMapLibre() first.');

    const defaultOptions: MapOptions = {
      container: mapId,
      style: `https://api.jawg.io/styles/7c47e08c-62b8-4523-a265-32267d4f8e9b.json?access-token=${this.jawgAccessToken}`,
      center: new this.maplibre.LngLat(0, 0),
      keyboard: true,
      minZoom: 3,
      zoom: 3,
      dragRotate: false,
      pitchWithRotate: false,
      touchZoomRotate: true,
    };

    const map = new this.maplibre.Map({ ...defaultOptions, ...options });
    map.touchZoomRotate.disableRotation();

    return map;
  }

  createMarker(coordinates: LngLat, options?: Partial<MarkerOptions>): Marker {
    if (!this.maplibre) throw new Error('MapLibre not loaded. Call loadMapLibre() first.');

    const element = document.createElement('div');
    element.style.backgroundImage = "url('assets/images/maps/location-marker.png')";
    element.style.width = '32px';
    element.style.height = '45px';
    element.style.backgroundSize = 'contain';

    const markerOptions: MarkerOptions = {
      element,
      anchor: 'bottom',
      draggable: true,
      ...options,
    };

    return new this.maplibre.Marker(markerOptions).setLngLat(coordinates);
  }
}
